/* eslint-disable react-hooks/rules-of-hooks */
import React from 'react';

import CurrencyFormField from '@components/form-fields/CurrencyFormField';
import NumberInputFormField from '@components/form-fields/NumberInputFormField';
import SelectFormField from '@components/form-fields/SelectFormField';
import TextInputFormField from '@components/form-fields/TextInputFormField';
import WidgetPaper from '@components/widgets/display-widget/WidgetPaper';
import WidgetSection from '@components/widgets/display-widget/WidgetSection';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ClearIcon from '@mui/icons-material/Clear';
import { MAIN_THEME } from '@styles';

import { FormValues, IBankersBlanketBondDetails } from '../types';

import { FieldArray, useFormikContext } from 'formik';
import { makeStyles } from 'tss-react/mui';

const YES_NO = { Yes: 'Yes', No: 'No' } as const;

const useStyles = makeStyles()(() => ({
  container: {
    width: '100%',
    backgroundColor: MAIN_THEME.color.white98,
    border: `1px solid ${MAIN_THEME.color.white92}`,
    borderRadius: '4px',
    padding: '16px',
    marginBottom: '14px',
    position: 'relative'
  },
  rowGrid: {
    display: 'grid',
    gap: 8,
    gridTemplateColumns: 'repeat(4, 1fr)'
  },
  removeBtn: {
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    position: 'absolute',
    top: 6,
    right: 6
  },
  addBtn: {
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    margin: '20px auto 0',
    display: 'block'
  }
}));

type Props = {
  status: string;
  currencySymbol: string;
};

const emptyRow: IBankersBlanketBondDetails = {
  branchName: '',
  isMain: false,
  isAgency: false,
  employeeNumber: undefined,
  underwritingYear: undefined,
  infedility: undefined,
  burglary: undefined,
  holdup: undefined,
  inSafe: undefined,
  inTransit: undefined,
  forgedCheques: undefined,
  counterfeitCurrencies: undefined,
  clause6: undefined,
  falsification: undefined,
  id: '',
  policyID: '',
  policyVersion: 0,
  policyVersionState: '',
  proposalID: ''
};

const BankersBlanketBondWidgetAtom: React.FC<Props> = ({
  status,
  currencySymbol
}) => {
  const { classes } = useStyles();
  const s = String(status || '').toUpperCase();
  const isLocked = s === 'CONFIRMED' || s === 'CANCELLED';

  const {
    values,
    setFieldValue,
    setFieldTouched,
    setFieldError,
    handleBlur,
    errors,
    touched
  } = useFormikContext<FormValues>();

  const rows = values.bankerDetails;

  const onNonNegativeBlur = async (
    path: string,
    raw: any,
    message = 'Value cannot be negative'
  ) => {
    const n = Number(raw);
    if (raw === '' || raw == null) return;
    if (!Number.isFinite(n)) return;
    if (n < 0) {
      await setFieldError(path, message);
      await setFieldTouched(path, true, false);
    }
  };

  const onWholeNumberBlur = async (
    path: string,
    raw: any,
    messageIfInvalid: string
  ) => {
    const n = Number(raw);
    if (raw === '' || raw == null) return;
    if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0) {
      await setFieldError(path, messageIfInvalid);
      await setFieldTouched(path, true, false);
    }
  };

  return (
    <WidgetPaper>
      <WidgetSection title="Bankers Blanket Bond Details" useSeparator={false}>
        <FieldArray
          name={'bankerDetails'}
          render={(arrayHelpers) => (
            <>
              {(rows ?? []).map((row, idx) => {
                const base = `${'bankerDetails'}[${idx}]`;

                return (
                  <div key={idx} className={classes.container}>
                    {!isLocked && (rows?.length ?? 0) > 1 && (
                      <button
                        className={classes.removeBtn}
                        onClick={() =>
                          rows.length > 1 && arrayHelpers.remove(idx)
                        }
                        type="button"
                        title="Remove Entry"
                      >
                        <ClearIcon fontSize="small" />
                      </button>
                    )}

                    <div className={classes.rowGrid}>
                      <TextInputFormField
                        name={`${base}.branchName`}
                        title="Branch Name*"
                        placeholder="Branch Name"
                        value={row.branchName}
                        onBlur={(e) => handleBlur(e)}
                        onChange={(e) =>
                          setFieldValue(
                            `${base}.branchName`,
                            e.target.value,
                            false
                          )
                        }
                        error={
                          (touched?.bankerDetails?.[idx] as any)?.branchName
                            ? (errors?.bankerDetails?.[idx] as any)?.branchName
                            : ''
                        }
                        disabled={isLocked}
                        material
                      />
                      <SelectFormField
                        name={`${base}.isMain`}
                        title="Main*"
                        placeholder="Is Main"
                        value={row.isMain ? 'Yes' : 'No'}
                        onBlur={(_, e) => handleBlur(e)}
                        onChange={(v) =>
                          setFieldValue(`${base}.isMain`, v === 'Yes', false)
                        }
                        error={
                          (touched?.bankerDetails?.[idx] as any)?.isMain
                            ? (errors?.bankerDetails?.[idx] as any)?.isMain
                            : ''
                        }
                        disabled={isLocked}
                        selectOptions={YES_NO}
                        material
                      />
                      <SelectFormField
                        name={`${base}.isAgency`}
                        title="Agency*"
                        placeholder="Is Agency"
                        value={row.isAgency ? 'Yes' : 'No'}
                        onBlur={(_, e) => handleBlur(e)}
                        onChange={(v) =>
                          setFieldValue(`${base}.isAgency`, v === 'Yes', false)
                        }
                        error={
                          (touched?.bankerDetails?.[idx] as any)?.isAgency
                            ? (errors?.bankerDetails?.[idx] as any)?.isAgency
                            : ''
                        }
                        disabled={isLocked}
                        selectOptions={YES_NO}
                        material
                      />
                      <NumberInputFormField
                        name={`${base}.employeeNumber`}
                        title="Employee Number*"
                        placeholder="Employee Number"
                        value={row.employeeNumber}
                        onBlur={(e) => {
                          handleBlur(e);
                          onWholeNumberBlur(
                            `${base}.employeeNumber`,
                            row.employeeNumber,
                            'Invalid Employee Number'
                          );
                        }}
                        onChange={(e) =>
                          setFieldValue(
                            `${base}.employeeNumber`,
                            e.target.value,
                            false
                          )
                        }
                        error={
                          (touched?.bankerDetails?.[idx] as any)?.employeeNumber
                            ? (errors?.bankerDetails?.[idx] as any)
                                ?.employeeNumber
                            : ''
                        }
                        disabled={isLocked}
                        material
                        maxDecimalPrecision={0}
                      />
                    </div>

                    <div className={classes.rowGrid} style={{ marginTop: 8 }}>
                      <NumberInputFormField
                        name={`${base}.underwritingYear`}
                        title="Underwriting Year*"
                        placeholder="Underwriting Year"
                        value={row.underwritingYear}
                        onBlur={(e) => {
                          handleBlur(e);
                          onWholeNumberBlur(
                            `${base}.underwritingYear`,
                            row.underwritingYear,
                            'Invalid Underwriting Year number'
                          );
                        }}
                        onChange={(e) =>
                          setFieldValue(
                            `${base}.underwritingYear`,
                            e.target.value,
                            false
                          )
                        }
                        error={
                          (touched?.bankerDetails?.[idx] as any)
                            ?.underwritingYear
                            ? (errors?.bankerDetails?.[idx] as any)
                                ?.underwritingYear
                            : ''
                        }
                        disabled={isLocked}
                        material
                        maxDecimalPrecision={0}
                      />
                      <CurrencyFormField
                        name={`${base}.infedility`}
                        title="Infidelity*"
                        placeholder="Infidelity"
                        value={row.infedility ?? ''}
                        onBlur={(e) => {
                          handleBlur(e);
                          onNonNegativeBlur(
                            `${base}.infedility`,
                            row.infedility
                          );
                        }}
                        onChange={(e) =>
                          setFieldValue(
                            `${base}.infedility`,
                            e.target.value,
                            false
                          )
                        }
                        error={
                          (touched?.bankerDetails?.[idx] as any)?.infedility
                            ? (errors?.bankerDetails?.[idx] as any)?.infedility
                            : ''
                        }
                        disabled={isLocked}
                        material
                        currencySymbol={currencySymbol}
                      />
                      <CurrencyFormField
                        name={`${base}.burglary`}
                        title="Burglary*"
                        placeholder="Burglary"
                        value={row.burglary ?? ''}
                        onBlur={(e) => {
                          handleBlur(e);
                          onNonNegativeBlur(`${base}.burglary`, row.burglary);
                        }}
                        onChange={(e) =>
                          setFieldValue(
                            `${base}.burglary`,
                            e.target.value,
                            false
                          )
                        }
                        error={
                          (touched?.bankerDetails?.[idx] as any)?.burglary
                            ? (errors?.bankerDetails?.[idx] as any)?.burglary
                            : ''
                        }
                        disabled={isLocked}
                        material
                        currencySymbol={currencySymbol}
                      />
                      <CurrencyFormField
                        name={`${base}.holdup`}
                        title="Hold Up*"
                        placeholder="Hold Up"
                        value={row.holdup ?? ''}
                        onBlur={(e) => {
                          handleBlur(e);
                          onNonNegativeBlur(`${base}.holdup`, row.holdup);
                        }}
                        onChange={(e) =>
                          setFieldValue(`${base}.holdup`, e.target.value, false)
                        }
                        error={
                          (touched?.bankerDetails?.[idx] as any)?.holdup
                            ? (errors?.bankerDetails?.[idx] as any)?.holdup
                            : ''
                        }
                        disabled={isLocked}
                        material
                        currencySymbol={currencySymbol}
                      />
                    </div>

                    <div className={classes.rowGrid} style={{ marginTop: 8 }}>
                      <CurrencyFormField
                        name={`${base}.inSafe`}
                        title="In Safe*"
                        placeholder="In Safe"
                        value={row.inSafe ?? ''}
                        onBlur={(e) => {
                          handleBlur(e);
                          onNonNegativeBlur(`${base}.inSafe`, row.inSafe);
                        }}
                        onChange={(e) =>
                          setFieldValue(`${base}.inSafe`, e.target.value, false)
                        }
                        error={
                          (touched?.bankerDetails?.[idx] as any)?.inSafe
                            ? (errors?.bankerDetails?.[idx] as any)?.inSafe
                            : ''
                        }
                        disabled={isLocked}
                        material
                        currencySymbol={currencySymbol}
                      />
                      <CurrencyFormField
                        name={`${base}.inTransit`}
                        title="In Transit*"
                        placeholder="In Transit"
                        value={row.inTransit ?? ''}
                        onBlur={(e) => {
                          handleBlur(e);
                          onNonNegativeBlur(`${base}.inTransit`, row.inTransit);
                        }}
                        onChange={(e) =>
                          setFieldValue(
                            `${base}.inTransit`,
                            e.target.value,
                            false
                          )
                        }
                        error={
                          (touched?.bankerDetails?.[idx] as any)?.inTransit
                            ? (errors?.bankerDetails?.[idx] as any)?.inTransit
                            : ''
                        }
                        disabled={isLocked}
                        material
                        currencySymbol={currencySymbol}
                      />
                      <CurrencyFormField
                        name={`${base}.forgedCheques`}
                        title="Forged Checks*"
                        placeholder="Forged Checks"
                        value={row.forgedCheques ?? ''}
                        onBlur={(e) => {
                          handleBlur(e);
                          onNonNegativeBlur(
                            `${base}.forgedCheques`,
                            row.forgedCheques
                          );
                        }}
                        onChange={(e) =>
                          setFieldValue(
                            `${base}.forgedCheques`,
                            e.target.value,
                            false
                          )
                        }
                        error={
                          (touched?.bankerDetails?.[idx] as any)?.forgedCheques
                            ? (errors?.bankerDetails?.[idx] as any)
                                ?.forgedCheques
                            : ''
                        }
                        disabled={isLocked}
                        material
                        currencySymbol={currencySymbol}
                      />
                      <CurrencyFormField
                        name={`${base}.counterfeitCurrencies`}
                        title="Counterfeit Currencies*"
                        placeholder="Counterfeit Currencies"
                        value={row.counterfeitCurrencies ?? ''}
                        onBlur={(e) => {
                          handleBlur(e);
                          onNonNegativeBlur(
                            `${base}.counterfeitCurrencies`,
                            row.counterfeitCurrencies
                          );
                        }}
                        onChange={(e) =>
                          setFieldValue(
                            `${base}.counterfeitCurrencies`,
                            e.target.value,
                            false
                          )
                        }
                        error={
                          (touched?.bankerDetails?.[idx] as any)
                            ?.counterfeitCurrencies
                            ? (errors?.bankerDetails?.[idx] as any)
                                ?.counterfeitCurrencies
                            : ''
                        }
                        disabled={isLocked}
                        material
                        currencySymbol={currencySymbol}
                      />
                    </div>

                    <div className={classes.rowGrid} style={{ marginTop: 8 }}>
                      <CurrencyFormField
                        name={`${base}.clause6`}
                        title="Clause 6*"
                        placeholder="Clause 6"
                        value={row.clause6 ?? ''}
                        onBlur={(e) => {
                          handleBlur(e);
                          onNonNegativeBlur(`${base}.clause6`, row.clause6);
                        }}
                        onChange={(e) =>
                          setFieldValue(
                            `${base}.clause6`,
                            e.target.value,
                            false
                          )
                        }
                        error={
                          (touched?.bankerDetails?.[idx] as any)?.clause6
                            ? (errors?.bankerDetails?.[idx] as any)?.clause6
                            : ''
                        }
                        disabled={isLocked}
                        material
                        currencySymbol={currencySymbol}
                      />
                      <CurrencyFormField
                        name={`${base}.falsification`}
                        title="Falsification*"
                        placeholder="Falsification"
                        value={row.falsification ?? ''}
                        onBlur={(e) => {
                          handleBlur(e);
                          onNonNegativeBlur(
                            `${base}.falsification`,
                            row.falsification
                          );
                        }}
                        onChange={(e) =>
                          setFieldValue(
                            `${base}.falsification`,
                            e.target.value,
                            false
                          )
                        }
                        error={
                          (touched?.bankerDetails?.[idx] as any)?.falsification
                            ? (errors?.bankerDetails?.[idx] as any)
                                ?.falsification
                            : ''
                        }
                        disabled={isLocked}
                        material
                        currencySymbol={currencySymbol}
                      />
                    </div>
                  </div>
                );
              })}

              {!isLocked && (
                <button
                  type="button"
                  className={classes.addBtn}
                  onClick={() => arrayHelpers.push({ ...emptyRow })}
                >
                  <AddCircleOutlineIcon fontSize="small" color="error" />
                </button>
              )}
            </>
          )}
        />
      </WidgetSection>
    </WidgetPaper>
  );
};

export { BankersBlanketBondWidgetAtom };
